import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { CartProvider } from "@/lib/cart";
import { AuthProvider } from "@/context/AuthContext";
import { ContentProvider } from "@/context/ContentContext";
import { Layout } from "@/components/Layout";
import Seo from "@/components/Seo";
import { scheduleGA } from "@/lib/analytics";
import { loadSiteOrigin } from "@/lib/siteConfig";

import Home from "@/pages/Home";
import CustomEtching from "@/pages/CustomEtching";
import Etchings from "@/pages/Etchings";
import Archive from "@/pages/Archive";
import ArtworkDetail from "@/pages/ArtworkDetail";
import Photography from "@/pages/Photography";
import { CollectionsIndex, CollectionGallery } from "@/pages/Collections";
import { RoutesIndex, RouteGallery } from "@/pages/Routes";
import Stories from "@/pages/Stories";
import StoryDetail from "@/pages/StoryDetail";
import HowWeMake from "@/pages/HowWeMake";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import PaymentSuccess from "@/pages/PaymentSuccess";
import InfoPage from "@/pages/InfoPage";
import AuthCallback from "@/pages/AuthCallback";
import RedirectOrNotFound from "@/pages/RedirectOrNotFound";

// Admin/SEO area (incl. recharts) is code-split into its own async chunk so public routes
// never download it. Loaded on demand when /admin is visited.
const Admin = React.lazy(() => import("@/pages/Admin"));

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Layout><Home /></Layout>} />
        <Route path="/custom-etching" element={<Layout><CustomEtching /></Layout>} />
        <Route path="/etchings" element={<Layout><Etchings /></Layout>} />
        <Route path="/etchings/archive" element={<Layout><Archive /></Layout>} />
        <Route path="/etchings/:slug" element={<Layout><ArtworkDetail /></Layout>} />
        <Route path="/photography" element={<Layout><Photography /></Layout>} />
        <Route path="/photography/collections" element={<Layout><CollectionsIndex /></Layout>} />
        <Route path="/photography/collections/:name" element={<Layout><CollectionGallery /></Layout>} />
        <Route path="/photography/routes" element={<Layout><RoutesIndex /></Layout>} />
        <Route path="/photography/routes/:slug" element={<Layout><RouteGallery /></Layout>} />
        <Route path="/stories" element={<Layout><Stories /></Layout>} />
        <Route path="/stories/:slug" element={<Layout><StoryDetail /></Layout>} />
        <Route path="/how-we-make-an-etching" element={<Layout><HowWeMake /></Layout>} />
        <Route path="/about" element={<Layout><About /></Layout>} />
        <Route path="/contact" element={<Layout><Contact /></Layout>} />
        <Route path="/cart" element={<Layout><Cart /></Layout>} />
        <Route path="/checkout" element={<Layout><Checkout /></Layout>} />
        <Route path="/payment/success" element={<Layout><PaymentSuccess /></Layout>} />
        <Route path="/shipping" element={<Layout><InfoPage kind="shipping" /></Layout>} />
        <Route path="/returns" element={<Layout><InfoPage kind="returns" /></Layout>} />
        <Route path="/terms" element={<Layout><InfoPage kind="terms" /></Layout>} />
        <Route path="/privacy" element={<Layout><InfoPage kind="privacy" /></Layout>} />
        <Route path="/admin/*" element={<React.Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]" data-testid="admin-loading"><div className="animate-pulse text-sm text-[#9a9793]">Loading…</div></div>}><><Seo title="Studio Admin — CaminoSapiens" description="Private studio administration area for CaminoSapiens." noindex /><Admin /></></React.Suspense>} />
        <Route path="*" element={<RedirectOrNotFound />} />
      </Routes>
    </>
  );
}

function App() {
  React.useEffect(() => { scheduleGA(); loadSiteOrigin(); }, []);
  return (
    <div className="App">
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <Toaster position="bottom-right" theme="light" />
            <ContentProvider>
              <AppRouter />
            </ContentProvider>
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
